export function formatGuidedFlowTemplate(template, params) {
  return template.replace(/\{(\w+)\}/g, (_, key) =>
    params[key] === undefined ? `{${key}}` : String(params[key]),
  );
}

export function clampSelfieDay(value) {
  const parsed = Number.parseInt(String(value), 10);
  if (!Number.isFinite(parsed)) return 28;
  return Math.min(31, Math.max(1, parsed));
}

export function apiErrorMessage(error, fallback) {
  if (typeof error === 'string') return error;
  if (error?.message) return error.message;
  return fallback;
}

/** Dia efectivo da selfie no mês (ex.: 31 → 30 em Abril). */
export function effectiveSelfieDayOfMonth(selfieDayOfMonth, referenceDate = new Date()) {
  const year = referenceDate.getFullYear();
  const month = referenceDate.getMonth() + 1;
  const daysInMonth = new Date(year, month, 0).getDate();
  return Math.min(clampSelfieDay(selfieDayOfMonth ?? 28), daysInMonth);
}

/**
 * True apenas no dia da selfie (igual ao lembrete: selfie_day_of_month == hoje).
 */
export function isSelfieDayDue(selfieDayOfMonth, referenceDate = new Date()) {
  if (selfieDayOfMonth == null || selfieDayOfMonth === '') return false;
  const effectiveDay = effectiveSelfieDayOfMonth(selfieDayOfMonth, referenceDate);
  return referenceDate.getDate() === effectiveDay;
}

/**
 * Selfie registada (alinhado com evaluate_step g2 no backend).
 * A timeline só inclui selfies concluídas — não usar só patrimonioTimeline.length.
 */
function isGuidedStepSatisfied(step, stepCompleted = {}, skipped = []) {
  return Boolean(stepCompleted[step]) || (skipped || []).includes(step);
}

/**
 * Fluxo concluído quando todos os passos *visíveis* estão feitos
 * (saltados contam como feitos; passos ocultos não bloqueiam).
 */
export function isGuidedFlowFinished(state, options = {}) {
  if (!state) return true;
  if (state.completed || state.step === 'completed') return true;

  const stepCompleted = state.step_completed || {};
  const skipped = state.skipped || [];
  const visible = filterVisibleGuidedSteps(state.order || [], {
    hasSnapshots: options.hasSnapshots,
    stepCompleted,
    selfieDayOfMonth: options.selfieDayOfMonth,
    selfieDayConfigured: options.selfieDayConfigured,
    referenceDate: options.referenceDate,
    currentCycle: options.currentCycle,
  });

  if (visible.length === 0) return true;
  return visible.every((step) => isGuidedStepSatisfied(step, stepCompleted, skipped));
}

export function hasGuidedFlowSelfie(options = {}) {
  const { hasSnapshots, stepCompleted } = options;
  return Boolean(hasSnapshots) || Boolean(stepCompleted?.g2_first_selfie);
}

/**
 * @param {object} options
 * @param {boolean} options.hasSnapshots
 * @param {object} [options.stepCompleted]
 * @param {number|null|undefined} options.selfieDayOfMonth
 * @param {boolean} [options.selfieDayConfigured]
 * @param {boolean|null|undefined} [options.hasActiveDebts]
 * @param {Date} [options.referenceDate]
 * @param {object|null} [options.currentCycle]
 */
export function filterVisibleGuidedSteps(order, options = {}) {
  const {
    hasSnapshots,
    stepCompleted,
    selfieDayConfigured = false,
    hasActiveDebts,
    referenceDate = new Date(),
    currentCycle = null,
  } = options;
  const steps = order || [];
  const hasSelfie = hasGuidedFlowSelfie({ hasSnapshots, stepCompleted });

  void hasActiveDebts;
  void referenceDate;
  void currentCycle;

  return steps.filter((step) => {
    if (step === 'g4_emergency_fund' && !hasSelfie) return false;
    // G2 após configurar o dia — disponível qualquer dia do calendário.
    if (step === 'g2_first_selfie' && !hasSelfie) {
      if (!selfieDayConfigured) return false;
    }
    // G11 (dívidas) e G7 (cofre) ficam sempre na jornada (skip / auto-evaluate no BE).
    return true;
  });
}

export function countCompletedGuidedSteps(order, stepCompleted, options = {}) {
  const visible = filterVisibleGuidedSteps(order, options);
  const skipped = options.skipped || [];
  return visible.filter((step) =>
    isGuidedStepSatisfied(step, stepCompleted, skipped),
  ).length;
}

/** Passo activo no wizard — primeiro pendente na ordem visível (saltados = feitos). */
export function resolveGuidedWizardStep(state, options = {}) {
  if (!state) return null;

  const visible = filterVisibleGuidedSteps(state.order, options);
  const stepCompleted = state.step_completed || {};
  const skipped = state.skipped || options.skipped || [];

  const firstPending = visible.find(
    (step) => !isGuidedStepSatisfied(step, stepCompleted, skipped),
  );
  if (firstPending) return firstPending;

  if (visible.includes(state.step)) {
    return state.step;
  }

  return visible[visible.length - 1] ?? state.step;
}

export function hasAnyCategoryBudget(categories) {
  if (!Array.isArray(categories)) return false;
  return categories.some((category) => {
    if (category?.is_archived || category?.is_system_pot) return false;
    const raw = category.default_monthly_budget;
    if (raw === null || raw === undefined || raw === '') return false;
    return Number(raw) > 0;
  });
}

/** @deprecated Selfie disponível a qualquer momento — já não bloqueia o fluxo guiado. */
export function isGuidedSelfieStepWaiting(_state, _options = {}) {
  return false;
}
